import "./style.scss";

import NoBeneficiary from "assets/img/svg/emptyState/noBeneficiary";
import NoExportBoard from "assets/img/svg/emptyState/noExportBoard";
import NoImportBoard from "assets/img/svg/emptyState/noImportBoard";
import NoNotification from "assets/img/svg/emptyState/noNotification";
import NoRepresentative from "assets/img/svg/emptyState/noRepresentative";
import NoTitle from "assets/img/svg/emptyState/noTitle";
import StatementError from "assets/img/svg/emptyState/statementError";
import Plus from "assets/img/svg/plus";
import { useCompanySettings } from "hooks/useCompanySettings";
import { usePermissions } from "hooks/usePermissions";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAppSelector } from "store/store";
import type { TPortalType } from "store/types";
import { EPortalType } from "store/types";

import variables from "../../../assets/styles/_variables.module.scss";
import StyledButton from "../StyledButton";

const buttonStyles = {
    "&.MuiButton-root": {
        borderRadius: variables["border-radius"],
        backgroundColor: "#303030",
        border: "none",
        color: "white",
        height: "1.9rem",
        "&:hover": {
            backgroundColor: "#464646",
        },
    },
};

type EmptyStateType =
    | "title"
    | "beneficiary"
    | "beneficiaries"
    | "channel"
    | "titleBoard"
    | "importBoard"
    | "exportBoard"
    | "stockBoard"
    | "payment"
    | "advancePayment"
    | "notifications"
    | "generic"
    | "authorTitles"
    | "company"
    | "articles"
    | "publications"
    | "beneficiaryPress"
    | "paymentPress"
    | "statements"
    | "stockValid"
    | "stockInvalid"
    | "stockNoLocations"
    | "representatives"
    | "heirs"
    | "paymentExports"
    | "advancePaid"
    | "advanceToPay"
    | "advanceUnpaid"
    | "noCumulativeSales"
    | "statementFailed"
    | "contracts"
    | "sales"
    | "integrations"
    | "partnerStatement"
    | "transferTermAdvances"
    | "transferTerms"
    | "transferTermSales"
    | "urssafBeneficiaries"
    | "documents"
    | "titleConsolidatedSales"
    | "statements_bportal"
    | "titles_bportal"
    | "maintenances";

interface EmptyStateProps {
    type: EmptyStateType;
    sx?: React.CSSProperties;
    onControlChange?: (value: "all" | "valid" | "invalid" | "paid" | "topay" | "unpaid") => void;
    handleAction?: any;
    portalType?: TPortalType;
}

const EmptyState = (props: EmptyStateProps) => {
    const [t] = useTranslation();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { id } = useParams();
    const { onControlChange, handleAction, portalType } = props;
    const { beneficiarCompagnies, beneficiar } = useAppSelector((state) => state.beneficiary);

    const mainBeneficiaryPortalType = beneficiarCompagnies[0]?.publisherType;

    const { companyInfos } = useCompanySettings();

    const { hasPermissions } = usePermissions();

    const currentPortal = useMemo(() => {
        if (portalType) return portalType;
        return companyInfos?.publisherType;
    }, [companyInfos, portalType]);

    const handleNavigateToArticles = () => {
        let stateParams = {};
        if (pathname.includes("publications")) stateParams = { publicationId: id };
        else if (pathname.includes("beneficiars") && id) {
            const beneficiaryId = Number(id);
            const isCurrentBeneficiary = beneficiar?.id == beneficiaryId;
            stateParams = {
                beneficiaryId,
                ...(isCurrentBeneficiary && {
                    beneficiaryFirstName: beneficiar.name,
                    beneficiarySurname: beneficiar.surname,
                    beneficiaryName: `${(beneficiar.surname ?? "").toUpperCase()} ${beneficiar.name ?? ""}`.trim(),
                }),
            };
        }
        navigate("/articles/new", { state: stateParams });
    };

    /* TODO:
     * - text: Edit all text of empty state for real text
     * - img:
     *  - channelImage: Edit image of channel for good image
     */
    const types: {
        [key: string | EmptyStateType]: {
            title: string;
            text: string;
            img: JSX.Element | null;
            btn: JSX.Element | null;
        };
    } = {
        title: {
            title: t("Titles.NoTitle"),
            text: t("Titles.NoTitleDescription"),
            img: <NoTitle />,
            btn: (
                <StyledButton
                    fullWidth
                    sx={{ ...buttonStyles, maxWidth: "260px" }}
                    onClick={() => navigate("/new-title")}
                >
                    <Plus />
                    {t("Titles.AddTitle")}
                </StyledButton>
            ),
        },
        publications: {
            title: t(`Titles.NoPublication_${currentPortal}`),
            text: t(`Titles.NoPublicationDescription_${currentPortal}`),
            img: <NoTitle />,
            btn: (
                <StyledButton
                    fullWidth
                    sx={{ ...buttonStyles, maxWidth: "260px" }}
                    onClick={() => navigate("/publications/new")}
                >
                    <Plus />
                    {t(`Titles.AddPublication_${currentPortal}`)}
                </StyledButton>
            ),
        },
        articles: {
            title: t(`Articles.NoArticle_${currentPortal}`),
            text: t(`Articles.NoArticleDescription_${currentPortal}`),
            img: <NoTitle />,
            btn: (
                <StyledButton
                    fullWidth
                    sx={{ ...buttonStyles, maxWidth: "260px" }}
                    onClick={handleNavigateToArticles}
                    disabled={!hasPermissions(["article:new"])}
                >
                    <Plus />
                    {t(`Articles.AddArticle_${currentPortal}`)}
                </StyledButton>
            ),
        },
        beneficiary: {
            title: t("Beneficiars.NoBeneficiars"),
            text: t("Beneficiars.NoBeneficiarDescription"),
            img: <NoBeneficiary />,
            btn: (
                <StyledButton fullWidth buttonType="primary" onClick={() => navigate("/beneficiaries/new")}>
                    <Plus />
                    {t("Beneficiars.AddBeneficiars")}
                </StyledButton>
            ),
        },
        // Filtered beneficiaries table with no match: no creation button
        beneficiaries: {
            title: t("Beneficiars.NoBeneficiars"),
            text: t("Generic.NoDataDescription"),
            img: <NoBeneficiary />,
            btn: null,
        },
        beneficiaryPress: {
            title: t("Beneficiars.NoBeneficiars"),
            text: t("Beneficiars.NoBeneficiarDescription"),
            img: <NoBeneficiary />,
            btn: (
                <StyledButton fullWidth buttonType="primary" onClick={() => navigate("/beneficiary/new")}>
                    <Plus />
                    {t("Beneficiars.AddBeneficiars")}
                </StyledButton>
            ),
        },
        channel: {
            title: t("Channels.NoChannel"),
            text: t("Channels.NoChannelDescription"),
            img: <NoBeneficiary />,
            btn: (
                <StyledButton
                    fullWidth
                    sx={{ ...buttonStyles, maxWidth: "260px" }}
                    onClick={() => navigate("/new-channel")}
                >
                    <Plus />
                    {t("Channels.AddChannel")}
                </StyledButton>
            ),
        },
        titleBoard: {
            title: t("Titles.NoElement"),
            text: t("Titles.NoElementDescription"),
            img: null,
            btn: (
                <StyledButton
                    fullWidth
                    sx={{ ...buttonStyles, maxWidth: "260px" }}
                    onClick={() => navigate("/new-title")}
                >
                    <Plus />
                    {t("Titles.AddTitle")}
                </StyledButton>
            ),
        },
        importBoard: {
            title: t("History.NoImport"),
            text: t("History.NoImportDescription"),
            img: <NoImportBoard />,
            btn: null,
        },
        exportBoard: {
            title: t("History.NoExport"),
            text: t("History.NoExportDescription"),
            img: <NoExportBoard />,
            btn: null,
        },
        stockBoard: {
            title: t("Stock.NoElement"),
            text: t("Stock.NoTitleDescription"),
            img: null,
            btn: (
                <StyledButton fullWidth sx={{ ...buttonStyles, maxWidth: "17rem" }} onClick={() => navigate("/titles")}>
                    <Plus />
                    {t("Stock.NoContractButton")}
                </StyledButton>
            ),
        },
        stockValid: {
            title: t("Stock.NoValid"),
            text: t("Stock.NoValidDescription"),
            img: null,
            btn: (
                <StyledButton
                    fullWidth
                    sx={{ ...buttonStyles, maxWidth: "16.25rem" }}
                    onClick={() => onControlChange && onControlChange("invalid")}
                >
                    {t("Stock.NoValidButton")}
                </StyledButton>
            ),
        },
        stockInvalid: {
            title: t("Stock.NoInvalid"),
            text: t("Stock.NoInvalidDescription"),
            img: null,
            btn: (
                <StyledButton
                    fullWidth
                    buttonType="outlined"
                    sx={{ ...buttonStyles, maxWidth: "260px" }}
                    onClick={() => onControlChange && onControlChange("all")}
                >
                    {t("Stock.NoInvalidButton")}
                </StyledButton>
            ),
        },
        stockNoLocations: {
            title: t("Stock.NoLocations"),
            text: t("Stock.NoLocationsDescription"),
            img: null,
            btn: (
                <StyledButton
                    fullWidth
                    buttonType="primary"
                    sx={{ ...buttonStyles, maxWidth: "20rem" }}
                    onClick={() => navigate("/settings/company")}
                >
                    <Plus />
                    {t("Stock.NoLocationsButton")}
                </StyledButton>
            ),
        },
        payment: {
            title: t("Titles.NoElement"),
            text: t("Payment.NoDataDescription"),
            img: null,
            btn: null,
        },
        paymentPress: {
            title: t("Titles.NoElement"),
            text: t("Payment.NoDataDescriptionPress"),
            img: null,
            btn: null,
        },
        paymentExports: {
            title: t("PaymentModal.EmptyStateTitle"),
            text: t("PaymentModal.EmptyStateSubtitle"),
            img: null,
            btn: null,
        },
        advancePayment: {
            title: t("AdvancePayment.EmptyStateGeneralTitle"),
            text: t("AdvancePayment.EmptyStateGeneralSubtitle"),
            img: null,
            btn: null,
        },
        advancePaid: {
            title: t("AdvancePayment.EmptyStatePaidTitle"),
            text: t("AdvancePayment.EmptyStatePaidSubtitle"),
            img: null,
            btn: null,
        },
        advanceToPay: {
            title: t("AdvancePayment.EmptyStateToPayTitle"),
            text: t("AdvancePayment.EmptyStateToPaySubtitle"),
            img: null,
            btn: (
                <StyledButton
                    fullWidth
                    sx={{ ...buttonStyles, maxWidth: "16.25rem" }}
                    onClick={() => onControlChange && onControlChange("unpaid")}
                >
                    {t("AdvancePayment.SeeUnpaidAdvances")}
                </StyledButton>
            ),
        },
        advanceUnpaid: {
            title: t("AdvancePayment.EmptyStateGeneralTitle"),
            text: t("AdvancePayment.EmptyStateGeneralSubtitle"),
            img: null,
            btn: (
                <StyledButton
                    fullWidth
                    sx={{ ...buttonStyles, maxWidth: "16.25rem" }}
                    onClick={() => onControlChange && onControlChange("all")}
                >
                    {t("AdvancePayment.SeeUnpaidAdvances")}
                </StyledButton>
            ),
        },
        authorTitles: {
            title: t("Titles.AuthorNoTitles"),
            text: t("Titles.AuthorNoTitlesDesc"),
            img: <NoTitle />,
            btn: null,
        },
        notifications: {
            title: t("Notifications.NoElement"),
            text: t("Notifications.NoElementDescription"),
            img: <NoNotification />,
            btn: null,
        },
        company: {
            title: t("BPSettings.NoCompany"),
            text: t("BPSettings.NoCompanyDescription"),
            img: <NoTitle />,
            btn: null,
        },
        documents: {
            title: t("Terms.NoDocumentTitle"),
            text: t("Terms.NoDocumentText"),
            img: <NoImportBoard />,
            btn: null,
        },
        generic: {
            title: t("Titles.NoElement"),
            text: t("Generic.NoDataDescription"),
            img: null,
            btn: null,
        },
        statements_bportal: {
            title: t("Titles.NoElement"),
            text: t(`StatementsTab.NoStatementsText_bportal`),
            img: null,
            btn: null,
        },
        titles_bportal: {
            title: t("Titles.NoElement"),
            text: t(`Titles.NoTitlesText_${mainBeneficiaryPortalType}`),
            img: null,
            btn: null,
        },
        statements: {
            title: t("StatementsTab.NoStatementsTitle"),
            text: t("StatementsTab.NoStatementsText"),
            img: null,
            btn: null,
        },
        representatives: {
            title: t("Beneficiars.NoAssociatedPersonTitle"),
            text: t("Beneficiars.NoAssociatedPersonDescription"),
            img: <NoRepresentative />,
            btn: null,
        },
        heirs: {
            title: t("Heirs.NoHeirsTitle"),
            text: t("Heirs.NoHeirsDescription"),
            img: <NoRepresentative />,
            btn: null,
        },
        noCumulativeSales: {
            title: t("Titles.NoDataTitle"),
            text: t("Titles.NoDataDescription"),
            img: null,
            btn: null,
        },
        statementFailed: {
            title: t("Summary.StatementErrorTitle"),
            text: t("Summary.StatementErrorText"),
            img: <StatementError />,
            btn: null,
        },
        contracts: {
            title: t("ContractValidation.NoContract"),
            text: t("ContractValidation.NoContractText"),
            img: null,
            btn: null,
        },
        avenants: {
            title: t("ContractValidation.NoAvenant"),
            text: t("ContractValidation.NoAvenantText"),
            img: null,
            btn: null,
        },
        sales: {
            title: t("Management.SaleDetails.NoSale"),
            text: t("Management.SaleDetails.NoSaleText"),
            img: null,
            btn: (
                <StyledButton fullWidth sx={{ ...buttonStyles, maxWidth: "16.25rem" }} onClick={() => handleAction()}>
                    {t("Management.AddCopyrights")}
                </StyledButton>
            ),
        },
        integrations: {
            title: t("Titles.NoDataTitle"),
            text: t("IntegrationsSettings.NoDataFound"),
            img: null,
            btn: null,
        },
        partnerStatement: {
            title: t("Titles.NoDataTitle"),
            text: t("PartnerStatements.DisplayedHere"),
            img: null,
            btn: null,
        },
        transferTermAdvances: {
            title: t("TransferTerms.EmptyStates.AdvanceTitle"),
            text: t("TransferTerms.EmptyStates.AdvanceSubtitle"),
            img: null,
            btn: null,
        },
        transferTerms: {
            title: t("TransferTerms.EmptyStates.TransferTermsTitle"),
            text: t("TransferTerms.EmptyStates.TransferTermsSubtitle"),
            img: null,
            btn: (
                <StyledButton
                    fullWidth
                    sx={{ ...buttonStyles, maxWidth: "16.25rem" }}
                    onClick={() => navigate("/transfer-terms/new")}
                >
                    {t("TransferTerms.EmptyStates.TransferTermsButton")}
                </StyledButton>
            ),
        },
        transferTermSales: {
            title: t("TransferTerms.EmptyStates.TransferTermSalesTitle"),
            text: t("TransferTerms.EmptyStates.TransferTermSalesSubtitle"),
            img: null,
            btn: (
                <StyledButton
                    fullWidth
                    sx={{ ...buttonStyles, maxWidth: "16.25rem" }}
                    onClick={() => navigate("/transfer-terms/new")}
                >
                    {t("TransferTerms.EmptyStates.TransferTermsButton")}
                </StyledButton>
            ),
        },
        transfereeTerms: {
            title: t("TransferTerms.EmptyStates.TransfereeTermTitle"),
            text: t("TransferTerms.EmptyStates.TransferTermsSubtitle"),
            img: null,
            btn: (
                <StyledButton
                    fullWidth
                    sx={{ ...buttonStyles, maxWidth: "16.25rem" }}
                    onClick={() => navigate("/transfer-terms/new", { state: { from: "transfereePage" } })}
                >
                    {t("TransferTerms.EmptyStates.TransferTermsButton")}
                </StyledButton>
            ),
        },
        urssafBeneficiaries: {
            title: t("Declarations.BeneficiariesTable.EmptyStateTitle"),
            text: t("Declarations.BeneficiariesTable.EmptyStateSubtitle", {
                page:
                    currentPortal === EPortalType.BOOK
                        ? t("Declarations.BeneficiariesTable.PublisherPaymentPage")
                        : t("NavigationBar.Beneficiars"),
            }),
            img: null,
            btn: (
                <StyledButton
                    fullWidth
                    sx={{ ...buttonStyles, maxWidth: "16.25rem" }}
                    onClick={() =>
                        currentPortal === EPortalType.BOOK
                            ? navigate("/management", { state: { tab: 1 } })
                            : navigate("/beneficiars")
                    }
                >
                    {t("Declarations.BeneficiariesTable.EmptyStateButton")}
                </StyledButton>
            ),
        },
        titleSales: {
            title: t("Management.SaleDetails.NoSale"),
            text: t("TitleSalesTable.EmptyStates.GeneralSalesText"),
            img: null,
            btn: (
                <StyledButton
                    fullWidth
                    sx={{ ...buttonStyles, maxWidth: "16.25rem" }}
                    onClick={() => navigate("/management")}
                >
                    {t("TitleSalesTable.EmptyStates.GeneralSalesBtn")}
                </StyledButton>
            ),
        },
        titleConsolidatedSales: {
            text: t("TitleSalesTable.EmptyStates.ConsolidatedSalesText"),
            title: t("TitleSalesTable.EmptyStates.ConsolidatedSalesTitle"),
            img: null,
            btn: null,
        },
        sageUploadHistory: {
            title: t("PaymentModal.SageHistory.EmptyStateTitle"),
            text: t("PaymentModal.SageHistory.EmptyStateSubtitle"),
            img: null,
            btn: null,
        },
        maintenances: {
            title: t("AdminPanel.MaintenanceEmptyTitle"),
            text: t("AdminPanel.MaintenanceEmptyText"),
            img: null,
            btn: props.handleAction ? (
                <StyledButton fullWidth sx={{ ...buttonStyles, maxWidth: "16.25rem" }} onClick={() => handleAction()}>
                    {t("AdminPanel.MaintenanceNew")}
                </StyledButton>
            ) : null,
        },
    };

    const emptyState = types[props.type] || types.generic;

    return (
        <div className="empty-state" style={props.sx}>
            {emptyState.img}
            <span className="empty-state-title">{emptyState.title}</span>
            <span className="empty-state-text">{emptyState.text}</span>
            {emptyState.btn}
        </div>
    );
};

export default EmptyState;
